import { useEffect, useRef, useState } from 'react';
import { fetchAtomSource } from '../../api/modules/components';
import type { AtomMeta } from '../../api/modules/components';
import { createAtomDemo, evalAtomClass } from './atom-demos';
import { LOGICAL_H, LOGICAL_W } from './demo-utils';
import type { DemoHandle } from './demo-utils';

interface Props {
  atom: AtomMeta;
}

/** atom 活预览：拉源码 → 沙箱 eval → 运行交互演示 */
export function AtomPreview({ atom }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState<Array<{ label: string; run: () => void }>>([]);
  const [hint, setHint] = useState('');
  const [state, setState] = useState<Record<string, string | number | boolean>>({});
  const stateRef = useRef<DemoHandle | null>(null);

  useEffect(() => {
    let disposed = false;
    let raf = 0;
    let handle: DemoHandle | null = null;
    setLoading(true);
    setError(null);
    setActions([]);
    setState({});

    (async () => {
      try {
        const payload = await fetchAtomSource(atom.key);
        const Cls = evalAtomClass(payload.source, payload.className);
        const canvas = canvasRef.current;
        if (!canvas || disposed) return;
        handle = createAtomDemo(atom.key, Cls, { canvas, width: LOGICAL_W, height: LOGICAL_H });
        stateRef.current = handle;
        if (disposed) { handle.destroy?.(); return; }
        setActions(handle.actions || []);
        setHint(handle.hint || '');

        const dpr = window.devicePixelRatio || 1;
        canvas.width = LOGICAL_W * dpr;
        canvas.height = LOGICAL_H * dpr;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        let last = performance.now();
        const loop = (now: number) => {
          if (disposed) return;
          const dt = Math.min(now - last, 100);
          last = now;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.clearRect(0, 0, LOGICAL_W, LOGICAL_H);
          handle?.update?.(dt);
          handle?.render?.(ctx, LOGICAL_W, LOGICAL_H);
          setState(handle?.getState?.() || {});
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
      } catch (e) {
        if (!disposed) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!disposed) setLoading(false);
      }
    })();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      handle?.destroy?.();
      stateRef.current = null;
    };
  }, [atom.key]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative rounded-xl border border-slate-700/60 bg-[#0d1220] overflow-hidden" style={{ width: LOGICAL_W, height: LOGICAL_H }}>
        <canvas ref={canvasRef} style={{ width: LOGICAL_W, height: LOGICAL_H, touchAction: 'none' }} />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">加载 atom 源码…</div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-rose-400 text-sm px-6 text-center">{error}</div>
        )}
      </div>
      {hint && <div className="text-xs text-slate-400 text-center max-w-[340px]">{hint}</div>}
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center max-w-[360px]">
          {actions.map((a) => (
            <button
              key={a.label}
              onClick={a.run}
              className="glow-btn px-3 py-1.5 text-xs rounded-lg"
              type="button"
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
      {Object.keys(state).length > 0 && (
        <div className="w-full max-w-[360px] rounded-lg border border-slate-700/60 bg-slate-900/60 px-3 py-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {Object.entries(state).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs">
                <span className="text-slate-500">{k}</span>
                <span className="text-cyan-300 font-mono">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
